// ── In-App Purchases (RevenueCat) ────────────────────────────────────────────
// Apple's App Store requires digital subscriptions to be sold through In-App
// Purchase, not Stripe. This wraps RevenueCat's Capacitor SDK so the app can buy
// a subscription natively on iOS; the RevenueCat webhook then grants the plan on
// our backend (see backend/controllers/revenueCatController.js). Native builds
// (iOS + Android) use store billing; only the web app uses Stripe.
//
// ⚠️ Plug in your own values (from the RevenueCat dashboard) before shipping:
//   • RC_API_KEY_IOS / RC_API_KEY_ANDROID — public SDK keys (safe to embed)
//   • PACKAGE_ID — the Offering package identifiers you create in RevenueCat
// These can also be supplied at build time via the REACT_APP_RC_* env vars.

import { isNative, isIOS, isAndroid } from './capacitor';
import { openExternalUrl } from './twa';
// STATIC import, deliberately not a lazy import().
//
// Capacitor's plugin proxy forwards every property access to native — including
// `then`. A dynamic import() makes webpack resolve the module namespace through
// promise machinery, which probes that `then`, fires
//     Uncaught (in promise) Error: "Purchases.then()" is not implemented on android
// and then never settles, because the bogus native call never invokes
// resolve/reject. The import hangs, configure() is never reached, and every
// purchase spins forever with no RevenueCat log to show for it.
// A static import is resolved at bundle time and sidesteps that entirely.
// Safe on web: it only registers the plugin; every call is gated by
// shouldUseIap(), so nothing native is ever invoked in a browser.
import { Purchases } from '@revenuecat/purchases-capacitor';

const RC_API_KEY_IOS     = process.env.REACT_APP_RC_IOS_KEY     || 'appl_YOUR_IOS_KEY';
const RC_API_KEY_ANDROID = process.env.REACT_APP_RC_ANDROID_KEY || 'goog_YOUR_ANDROID_KEY';

// our plan + billing → the RevenueCat *package* identifier in your Offering.
const PACKAGE_ID = {
  basic_monthly:  'basic_monthly',
  basic_annual:   'basic_annual',
  family_monthly: 'family_monthly',
  family_annual:  'family_annual',
};

// True when purchases on THIS platform must go through the app store (IAP)
// instead of Stripe. Both the iOS App Store and Google Play require store
// billing for digital goods, so every native build uses IAP; web uses Stripe.
// Escape hatch: set REACT_APP_IAP_ANDROID=0 to force Android back to Stripe.
export const shouldUseIap = () => {
  if (!isNative()) return false;
  if (isIOS()) return true;
  return process.env.REACT_APP_IAP_ANDROID !== '0';
};

// Who takes the money on this device, for customer-facing copy: 'the App
// Store' / 'Google Play' in the apps, null on the web (Stripe). Payment wording
// must follow the actual flow — App Review rejected the app (3.1.1) because the
// gift page said "Secure payment via Stripe" beside a button that bought
// through Apple, which reads as selling digital goods outside In-App Purchase.
export const paymentStoreName = () => (shouldUseIap() ? (isIOS() ? 'the App Store' : 'Google Play') : null);

let _configured = false;
let _lastInitError = null;
let _appUserID = null;

// How long to wait on a store lookup before giving up.
const IAP_TIMEOUT_MS = Number(process.env.REACT_APP_IAP_TIMEOUT_MS) || 15000;

// PRODUCT_CATEGORY.NON_SUBSCRIPTION. getProducts() defaults to SUBSCRIPTION on
// Android, so one-time products (top-ups + gifts) come back EMPTY unless the
// category is passed — Play is otherwise asked for a *subscription* with that
// id and finds nothing. Ignored on iOS, which is why this only breaks Android.
// Literal rather than the enum, to keep the import surface minimal.
const NON_SUBSCRIPTION = 'NON_SUBSCRIPTION';

// RevenueCat waits on Google Play's BillingClient, which retries forever when
// billing is unavailable — most often because the build wasn't installed from
// Play (a sideloaded/Android Studio build can never reach Play Billing). That
// left the purchase button spinning with no error and no way out, so every
// store *lookup* is bounded. The purchase call itself is deliberately NOT
// wrapped: the user may sit on Google's payment sheet for minutes.
const withTimeout = (promise, what) => Promise.race([
  promise,
  new Promise((_, reject) => setTimeout(() => {
    console.error(
      `[iap] ${what} timed out after ${IAP_TIMEOUT_MS}ms. On Android this usually means Google Play ` +
      'Billing is unavailable: install the app from Play (internal testing) with a licence-tester ' +
      'account, and make sure the products are Active and mapped in RevenueCat.'
    );
    reject(new Error('The store isn’t responding. Please check your connection and try again.'));
  }, IAP_TIMEOUT_MS)),
]);

// Configure the SDK once and associate purchases with the logged-in user, so the
// webhook's event.app_user_id maps straight to our User _id. Safe to call on
// every auth change — no-ops on non-IAP platforms, re-uses the existing config.
export const initIap = async (appUserID) => {
  if (!shouldUseIap()) return;
  if (appUserID) _appUserID = String(appUserID);
  try {
    const apiKey = isIOS() ? RC_API_KEY_IOS : RC_API_KEY_ANDROID;
    // Verbose store logs go to logcat / the Xcode console only — never to the
    // user. Deliberately NOT awaited: a bridge call that never calls back would
    // otherwise stall initialisation *before* configure() ever runs, leaving the
    // SDK unconfigured with no error to report — which looks exactly like
    // "store not ready". Disable with REACT_APP_IAP_DEBUG=0.
    if (process.env.REACT_APP_IAP_DEBUG !== '0') {
      try { Promise.resolve(Purchases.setLogLevel({ level: 'DEBUG' })).catch(() => {}); } catch (_) {}
    }
    if (!_configured) {
      console.log('[iap] configuring RevenueCat', { platform: isIOS() ? 'ios' : 'android', keyPrefix: String(apiKey).slice(0, 5), appUserID: String(_appUserID || '') });
      await withTimeout(
        Purchases.configure({ apiKey, appUserID: _appUserID || undefined }),
        'configure'
      );
      _configured = true;
      _lastInitError = null;
      console.log('[iap] configured OK');
    } else if (_appUserID) {
      await withTimeout(Purchases.logIn({ appUserID: _appUserID }), 'logIn');
    }
  } catch (e) {
    // Non-fatal: the app still works, purchases just won't be available. Keep
    // the reason so a later purchase attempt can say something useful instead
    // of failing generically.
    _lastInitError = e?.message || String(e);
    console.warn('[iap] init failed:', _lastInitError);
  }
};

// Why in-app purchases are unavailable, if they are. Null when things look OK.
export const iapUnavailableReason = () => {
  if (!shouldUseIap()) return null;
  if (!_configured) {
    return _lastInitError
      ? `The store could not be reached (${_lastInitError}).`
      : 'The store isn’t ready yet. Please try again in a moment.';
  }
  return null;
};

// Guarantee the SDK is configured before buying. initIap() runs at login and can
// legitimately still be in flight (or have failed) when the user taps Buy, so
// retry here and wait for it rather than refusing the purchase outright — a tap
// is exactly the moment it's worth blocking on. Throws a real reason if it
// still can't configure.
const ensureConfigured = async () => {
  if (_configured) return;
  await initIap(_appUserID);
  if (!_configured) {
    throw new Error(iapUnavailableReason() || 'The store isn’t ready yet. Please try again in a moment.');
  }
};

export const logoutIap = async () => {
  // Forget the account first, even if the SDK never configured. A stale id here
  // would be re-applied by ensureIdentified() and attribute the NEXT person's
  // purchase on this device to the previous account.
  _appUserID = null;
  if (!shouldUseIap() || !_configured) return;
  try {
    await withTimeout(Purchases.logOut(), 'logOut');
  } catch (_) { /* signing out locally must never be blocked by the store */ }
};

// The available packages from your current RevenueCat Offering.
export const getIapPackages = async () => {
  const res = await withTimeout(Purchases.getOfferings(), 'getOfferings');
  return res?.current?.availablePackages || [];
};

// Localized store price strings keyed by product identifier (e.g.
// { basic_monthly: '₹499', topup_story: '$3.99' }). These come straight from
// the App Store / Play Store, already in the user's currency. Native only —
// returns {} on web (which uses Stripe prices). Pass consumable product ids
// (top-ups/gifts) as `extraProductIds` since they live outside the Offering.
// The Offering package for one of our plan keys ('basic_monthly' …). Package id
// first, then the store product id. Google reports that as
// "<subscriptionId>:<basePlanId>" — e.g. "basic:basic-monthly" — so the part
// before the colon, and the base plan with the product prefix dropped
// ("basic" + "monthly"), are accepted too.
//
// purchaseIap AND both price lookups use this, so the price on a button is the
// price of the exact product that button buys. The price lookups used to key
// by the store product id instead, which on Android ("basic:basic-monthly")
// never matched the page's 'basic_monthly' — Android showed our backend price
// rather than what Google would charge.
const findPackage = (packages, key) => {
  const pkgId = PACKAGE_ID[key] || key;
  const matches = (id) => {
    if (!id) return false;
    const [base, rawTail = ''] = String(id).split(':');
    const tail = (rawTail.startsWith(`${base}-`) ? rawTail.slice(base.length + 1) : rawTail).replace(/yearly/i, 'annual');
    return id === pkgId || base === pkgId || `${base}_${tail}`.replace(/-/g, '_') === pkgId;
  };
  return packages.find(p => p.identifier === pkgId)
    || packages.find(p => matches(p.product?.identifier))
    || packages.find(p => matches(p.identifier))
    || null;
};

// Consumables come back as "<productId>" or, on Google, "<productId>:<option>".
const baseId = (id) => String(id || '').split(':')[0];

export const getIapPriceMap = async (extraProductIds = []) => {
  if (!shouldUseIap()) return {};
  const map = {};
  try {
    const packages = await getIapPackages();
    packages.forEach((p) => {
      const pid = p?.product?.identifier;
      if (pid && p.product.priceString) map[pid] = p.product.priceString;
    });
    Object.keys(PACKAGE_ID).forEach((key) => {
      const p = findPackage(packages, key);
      if (p?.product?.priceString) map[key] = p.product.priceString;
    });
  } catch (_) {}
  if (extraProductIds.length) {
    try {
      const { products } = await withTimeout(
        Purchases.getProducts({ productIdentifiers: extraProductIds, type: NON_SUBSCRIPTION }), 'getProducts(prices)'
      );
      (products || []).forEach((pr) => {
        if (pr?.identifier && pr.priceString) {
          map[pr.identifier] = pr.priceString;
          if (!map[baseId(pr.identifier)]) map[baseId(pr.identifier)] = pr.priceString;
        }
      });
    } catch (_) {}
  }
  return map;
};

// Richer variant: { productId: { priceString, price, currencyCode } } — used
// where numeric amounts are needed (e.g. computing annual savings). Native only.
export const getIapProductInfo = async (extraProductIds = []) => {
  if (!shouldUseIap()) return {};
  const map = {};
  const add = (pid, product) => {
    if (pid && product?.priceString) {
      const intro = product.introPrice;
      map[pid] = {
        productId:    product.identifier,
        priceString:  product.priceString,
        price:        product.price,
        currencyCode: product.currencyCode,
        // The store's introductory offer on THIS product — the only free trial
        // the store will actually give on the apps. Null when none is set up.
        intro: intro ? {
          price: intro.price, periodUnit: intro.periodUnit, periodNumberOfUnits: intro.periodNumberOfUnits, cycles: intro.cycles,
        } : null,
      };
    }
  };
  try {
    const packages = await getIapPackages();
    packages.forEach((p) => add(p?.product?.identifier, p?.product));
    Object.keys(PACKAGE_ID).forEach((key) => {
      const p = findPackage(packages, key);
      if (p) add(key, p.product);
    });
  } catch (_) {}
  if (extraProductIds.length) {
    try {
      const { products } = await withTimeout(
        Purchases.getProducts({ productIdentifiers: extraProductIds, type: NON_SUBSCRIPTION }), 'getProducts(priceInfo)'
      );
      (products || []).forEach((pr) => {
        add(pr?.identifier, pr);
        if (!map[baseId(pr?.identifier)]) add(baseId(pr?.identifier), pr);
      });
    } catch (_) {}
  }
  return map;
};

// Whether this store account can still get each product's introductory offer:
// { [productId]: true|false }. iOS only — StoreKit knows per Apple ID. Google
// returns only offers the account is eligible for, so on Android the product's
// introPrice already answers it. Unknown counts as not eligible: never promise
// a free trial the store might not give.
export const getIntroEligibility = async (productIds = []) => {
  if (!shouldUseIap() || !isIOS() || !productIds.length) return {};
  try {
    await ensureConfigured();
    const res = await withTimeout(
      Purchases.checkTrialOrIntroductoryPriceEligibility({ productIdentifiers: productIds }),
      'checkTrialOrIntroductoryPriceEligibility'
    );
    const out = {};
    Object.entries(res || {}).forEach(([id, e]) => { out[id] = e?.status === 2; }); // INTRO_ELIGIBILITY_STATUS_ELIGIBLE
    return out;
  } catch (_) {
    return {};
  }
};

const isUserCancelled = (err) =>
  err?.code === 'PURCHASE_CANCELLED' ||
  err?.userCancelled === true ||
  /cancel/i.test(err?.message || '');

// ── Customer-facing wording for store errors ─────────────────────────────────
// The raw RevenueCat message used to go straight into the "Purchase failed"
// dialog. For a configuration problem that meant showing a paying customer:
//   "There is an issue with your configuration. Check the underlying error for
//    more details. More information: https://rev.cat/sdk-troubleshooting ..."
// App Review rejected the app over exactly that dialog. The technical detail is
// kept in the console (and reaches Sentry); the customer gets a sentence they
// can act on. Codes are RevenueCat's PURCHASES_ERROR_CODE values.
const STORE_UNAVAILABLE = "Purchases aren't available right now. Please try again in a little while.";
const IN_USE_ELSEWHERE  = 'This store purchase is already linked to a different Dreamy Bedtime account. Sign in to that account to use it, or contact support@dreamybedtime.com.';
const FRIENDLY = {
  '2':  "The App Store couldn't complete the purchase. Please try again.",         // STORE_PROBLEM
  '3':  'Purchases are not allowed on this device. Check Screen Time restrictions.', // NOT_ALLOWED
  '5':  STORE_UNAVAILABLE,                                                          // PRODUCT_NOT_AVAILABLE
  '6':  'You already own this. Try "Restore purchases" in Settings.',               // ALREADY_PURCHASED
  '7':  IN_USE_ELSEWHERE,                                                           // RECEIPT_ALREADY_IN_USE
  '10': "Couldn't reach the App Store. Check your connection and try again.",       // NETWORK
  '13': IN_USE_ELSEWHERE,                                                           // RECEIPT_IN_USE_BY_OTHER_SUBSCRIBER
  '15': 'A purchase is already in progress. Please wait a moment.',                 // OPERATION_ALREADY_IN_PROGRESS
  '17': STORE_UNAVAILABLE,                                                          // INVALID_APPLE_SUBSCRIPTION_KEY
  '20': 'Your purchase is waiting for approval. It will unlock once approved.',     // PAYMENT_PENDING
  '23': STORE_UNAVAILABLE,                                                          // CONFIGURATION
  '32': "Couldn't reach the App Store. Check your connection and try again.",       // PRODUCT_REQUEST_TIMED_OUT
  '35': "Couldn't reach the App Store. Check your connection and try again.",       // OFFLINE
};

export const friendlyIapError = (err) => {
  const code = String(err?.code ?? err?.errorCode ?? '');
  // Keep the full developer-facing detail where a developer will see it.
  console.error('[iap] store error', code || '(no code)', err?.message, err?.underlyingErrorMessage || '');
  // The code rides along in the message. Customers can ignore it; without it a
  // screenshot of a failed purchase could not tell "store misconfigured" (23)
  // from "no connection" (10) — every cause reads the same once made friendly.
  // Not for 20: waiting on Ask to Buy approval is not an error.
  if (FRIENDLY[code]) return code === '20' ? FRIENDLY[code] : `${FRIENDLY[code]} (Error ${code})`;
  // No code, but the message is plainly a configuration/offerings problem.
  if (/configuration|offerings|could not be fetched|rev\.cat/i.test(err?.message || '')) return STORE_UNAVAILABLE;
  return code
    ? `The purchase could not be completed. Please try again. (Error ${code})`
    : 'The purchase could not be completed. Please try again.';
};

// Every purchase must be recorded against the signed-in account. RevenueCat
// attributes it to whichever app user id the SDK holds at that moment, and the
// webhook can only map our own ids back to an account. After a fresh sign-in the
// SDK was never identified (AuthContext keyed off a field the sign-in response
// did not have), so purchases went through as "$RCAnonymousID:…": Apple charged
// the customer, the webhook could not tell whose purchase it was, and the
// account stayed on Free. So check right before buying, rather than trusting
// that sign-in got there first.
const ensureIdentified = async () => {
  await ensureConfigured();
  if (!_appUserID) throw new Error('Please sign out and sign back in, then try again.');
  try {
    const { appUserID } = await withTimeout(Purchases.getAppUserID(), 'getAppUserID');
    if (appUserID !== _appUserID) {
      await withTimeout(Purchases.logIn({ appUserID: _appUserID }), 'logIn');
    }
  } catch (err) {
    // A store error gets the friendly wording; our own timeout already has it.
    throw err?.code != null ? new Error(friendlyIapError(err)) : err;
  }
};

/**
 * The store product the user is subscribed to right now, or null.
 *
 * This is what Play replaces. Both kinds of switch need it: Basic→Family, where
 * the two are separate subscription ids, and monthly→annual, where Google
 * reports the product as "<subscriptionId>:<basePlanId>" (e.g. "basic:basic-
 * monthly") and only the base plan differs. An exact match is the one case with
 * nothing to replace — the user already owns precisely this product.
 */
const currentStoreSubscriptionId = async (buyingProductId) => {
  try {
    const { customerInfo } = await withTimeout(Purchases.getCustomerInfo(), 'getCustomerInfo');
    const active = customerInfo?.activeSubscriptions || [];
    const other = active.filter(id => id !== buyingProductId);
    // With more than one active subscription (which the missing product-change
    // info could itself have caused), replace the one on the same subscription
    // id if there is one, so the switch lands where the user expects.
    const sameBase = String(buyingProductId || '').split(':')[0];
    return other.find(id => String(id).split(':')[0] === sameBase) || other[0] || null;
  } catch (err) {
    // Never block a purchase on this. Worst case we fall back to the old
    // behaviour, which still completes — it just does not replace anything.
    console.warn('[iap] could not read the current subscription for a plan switch:', err?.message);
    return null;
  }
};

// Buy a subscription. plan = 'basic'|'family', billing = 'monthly'|'annual'.
// Resolves { ok: true } on success, { ok: false, cancelled: true } if the user
// backed out, or throws a friendly Error otherwise.
export const purchaseIap = async (plan, billing) => {
  await ensureIdentified();
  const key   = `${plan}_${billing === 'annual' ? 'annual' : 'monthly'}`;
  const pkgId = PACKAGE_ID[key] || key;
  // Loading the offerings is where a configuration problem surfaces — "None of
  // the products registered in the RevenueCat dashboard could be fetched" — and
  // it used to sit outside the try below, so that raw developer message was
  // shown to the customer. That exact dialog is what App Review rejected.
  let packages;
  try {
    packages = await getIapPackages();
  } catch (err) {
    throw new Error(friendlyIapError(err));
  }
  const aPackage = findPackage(packages, key);
  if (!aPackage) {
    console.error('[iap] no package matched', pkgId, '— available:', packages.map(p => `${p.identifier} (${p.product?.identifier})`));
    throw new Error('This plan isn’t available for purchase right now. Please try again later.');
  }
  // Switching plans on Google Play is NOT just "buy the other product". Play
  // needs to be told which subscription is being replaced; without it the old
  // subscription keeps running and renewing alongside the new one, so a Basic
  // subscriber who upgraded to Family was billed for BOTH — or Play refused the
  // purchase outright as "item already owned" (error 6) when the two share a
  // subscription id, which is what a monthly→annual switch looks like.
  //
  // iOS needs nothing here: StoreKit crossgrades automatically, but only for
  // products in the SAME App Store subscription group. All four subscriptions
  // must sit in one group, or the App Store will happily sell a second one too.
  const opts = { aPackage };
  if (isAndroid()) {
    const oldProductIdentifier = await currentStoreSubscriptionId(aPackage.product?.identifier);
    if (oldProductIdentifier) {
      // WITH_TIME_PRORATION mirrors the web switch, which Stripe applies with
      // proration_behavior: 'create_prorations' — effective immediately, unused
      // time on the old plan credited toward the new one. Keeping the two paths
      // on the same rule means a switch means the same thing on every platform.
      opts.storeProductChangeInfo = { oldProductIdentifier, replacementMode: 'WITH_TIME_PRORATION' };
    }
  }
  try {
    const { customerInfo } = await Purchases.purchasePackage(opts);
    return { ok: true, customerInfo };
  } catch (err) {
    if (isUserCancelled(err)) return { ok: false, cancelled: true };
    throw new Error(friendlyIapError(err));
  }
};

// Top-up pack id → App Store / Play consumable product identifier. Create these
// as Consumable IAPs in App Store Connect and mirror the ids here (or via env).
export const TOPUP_PRODUCT_ID = {
  story:     process.env.REACT_APP_RC_TOPUP_STORY     || 'topup_story',
  narration: process.env.REACT_APP_RC_TOPUP_NARRATION || 'topup_narration',
  voice:     process.env.REACT_APP_RC_TOPUP_VOICE     || 'topup_voice',
  regen:     process.env.REACT_APP_RC_TOPUP_REGEN     || 'topup_regen',
  photo:     process.env.REACT_APP_RC_TOPUP_PHOTO     || 'topup_photo',
};

// Buy a one-time (consumable) store product by identifier — used for top-ups and
// gifts. The RevenueCat webhook credits/delivers server-side, so we only need the
// purchase to complete here.
export const purchaseProduct = async (productId) => {
  await ensureIdentified();
  let products;
  try {
    ({ products } = await withTimeout(
      Purchases.getProducts({ productIdentifiers: [productId], type: NON_SUBSCRIPTION }), `getProducts(${productId})`
    ));
  } catch (err) {
    throw new Error(friendlyIapError(err));
  }
  // Google may report one-time products as "<productId>:<purchaseOption>".
  const product = (products || []).find(p => p.identifier === productId)
    || (products || []).find(p => String(p.identifier).split(':')[0] === productId)
    || (products || [])[0];
  if (!product) {
    console.error('[iap] product not found:', productId, '— returned:', (products || []).map(p => p.identifier));
    throw new Error('This item isn’t available for purchase right now.');
  }
  try {
    await Purchases.purchaseStoreProduct({ product });
    return { ok: true };
  } catch (err) {
    if (isUserCancelled(err)) return { ok: false, cancelled: true };
    throw new Error(friendlyIapError(err));
  }
};

// Open the platform's native "manage subscriptions" screen. IAP subscriptions
// are managed in the App Store / Play Store, not the Stripe billing portal.
export const manageSubscriptionsIap = () => {
  const url = isIOS()
    ? 'https://apps.apple.com/account/subscriptions'
    : 'https://play.google.com/store/account/subscriptions';
  // openExternalUrl handles the WebView case: window.open('_blank') is a silent
  // no-op in Capacitor, so "Manage Subscription" used to do nothing and users
  // had no way to reach the store to cancel.
  openExternalUrl(url);
};

// Restore previous purchases (Apple requires an explicit restore path).
export const restoreIap = async () => {
  await ensureIdentified();
  // Bounded: the Restore button awaits this, so an unresolved store call would
  // leave it spinning forever — the same failure the purchase flow just had.
  const { customerInfo } = await withTimeout(Purchases.restorePurchases(), 'restorePurchases');
  const active = customerInfo?.entitlements?.active || {};
  return { ok: true, hasActive: Object.keys(active).length > 0, customerInfo };
};
